import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

export default function SocialAuthButtons({ role = "Super Admin" }) {
  const navigate = useNavigate();
  const { socialLogin } = useAuth();
  const [activeProvider, setActiveProvider] = useState(null);

  const submitToBackend = async (provider, email, name, avatar) => {
    const res = await socialLogin({
      provider,
      email: email.trim().toLowerCase(),
      name: name.trim(),
      avatar: avatar || null,
      role,
    });

    if (res.success) {
      toast.success(`Signed in as ${role} successfully!`);
      setTimeout(() => {
        navigate(res.homeRoute || "/admin/dashboard");
      }, 400);
    } else {
      toast.error(res.message || `Failed to sign in with ${provider}`);
    }
  };

  const triggerGoogleAuth = async () => {
    try {
      setActiveProvider("google");
      const googleClientId =
        process.env.REACT_APP_GOOGLE_CLIENT_ID ||
        "641839148483-ss02eou2j4qckdl73n87msf0ime7o8s0.apps.googleusercontent.com";

      // Dynamically load Google Identity Services if needed and wait for accounts.oauth2
      if (!window.google?.accounts?.oauth2) {
        await new Promise((resolve) => {
          let script = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
          if (!script) {
            script = document.createElement("script");
            script.src = "https://accounts.google.com/gsi/client";
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
          }
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if (window.google?.accounts?.oauth2 || attempts > 40) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (window.google?.accounts?.oauth2) {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: googleClientId,
          scope: "email profile openid",
          callback: async (tokenResponse) => {
            if (tokenResponse.error || !tokenResponse.access_token) {
              console.warn("Google sign-in cancelled or error:", tokenResponse.error);
              setActiveProvider(null);
              return;
            }
            try {
              const userInfoRes = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
              });
              const userInfo = await userInfoRes.json();
              if (!userInfo.email) {
                throw new Error("Could not retrieve email from Google account");
              }
              await submitToBackend("google", userInfo.email, userInfo.name || userInfo.email.split("@")[0], userInfo.picture);
            } catch (fetchErr) {
              console.warn("Failed to fetch Google profile:", fetchErr);
              toast.error(fetchErr.message || "Failed to load Google profile");
            } finally {
              setActiveProvider(null);
            }
          },
        });

        client.requestAccessToken({ prompt: "select_account" });
      } else {
        throw new Error("Google Identity services could not be initialized");
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      toast.error(err.message || "Google sign-in failed");
      setActiveProvider(null);
    }
  };

  const triggerAppleAuth = async () => {
    try {
      setActiveProvider("apple");
      const appleClientId =
        process.env.REACT_APP_APPLE_CLIENT_ID ||
        "com.briq.construction.site.app.web";

      // Dynamically load Apple JS SDK if not present
      if (!window.AppleID?.auth) {
        await new Promise((resolve) => {
          let script = document.querySelector('script[src*="appleid.auth.js"]');
          if (!script) {
            script = document.createElement("script");
            script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
          }
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if (window.AppleID?.auth || attempts > 40) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      const winApple = window.AppleID;
      if (winApple?.auth) {
        winApple.auth.init({
          clientId: appleClientId,
          scope: "name email",
          redirectURI: window.location.origin + "/login",
          usePopup: true,
        });

        const res = await winApple.auth.signIn();
        let appleEmail = res.user?.email;
        if (!appleEmail && res.authorization?.id_token) {
          try {
            const tokenParts = res.authorization.id_token.split(".");
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              appleEmail = payload.email;
            }
          } catch (tokenErr) {
            console.warn("Could not parse id_token payload:", tokenErr);
          }
        }

        if (!appleEmail) {
          throw new Error("Apple account did not return an email address.");
        }

        const firstName = res.user?.name?.firstName || "";
        const lastName = res.user?.name?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim() || appleEmail.split("@")[0] || "Apple User";
        await submitToBackend("apple", appleEmail, fullName);
      } else {
        const redirectUrl = `https://appleid.apple.com/auth/authorize?client_id=${encodeURIComponent(appleClientId)}&redirect_uri=${encodeURIComponent(window.location.origin + "/login")}&response_type=code%20id_token&scope=name%20email&response_mode=form_post`;
        window.location.href = redirectUrl;
      }
    } catch (err) {
      console.error("Apple sign-in error:", err);
      toast.error(err.message || "Apple sign-in failed");
      setActiveProvider(null);
    }
  };

  return (
    <div className="space-y-2.5 w-full">
      {/* Continue with Google */}
      <button
        type="button"
        disabled={activeProvider !== null}
        onClick={triggerGoogleAuth}
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 font-semibold text-[#111827] text-sm shadow-xs transition-all cursor-pointer disabled:opacity-60"
      >
        {activeProvider === "google" ? (
          <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
        )}
        <span>{activeProvider === "google" ? "Connecting to Google..." : "Continue with Google"}</span>
      </button>

      {/* Continue with Apple */}
      <button
        type="button"
        disabled={activeProvider !== null}
        onClick={triggerAppleAuth}
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg bg-black hover:bg-neutral-900 active:bg-neutral-800 font-semibold text-white text-sm shadow-xs transition-all cursor-pointer disabled:opacity-60"
      >
        {activeProvider === "apple" ? (
          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.38c.62-.75 1.04-1.8 0.93-2.85-.9.04-1.99.6-2.63 1.35-.57.65-.97 1.71-.84 2.73 1 .08 2.02-.51 2.54-1.23z" />
          </svg>
        )}
        <span>{activeProvider === "apple" ? "Connecting to Apple..." : "Continue with Apple"}</span>
      </button>
    </div>
  );
}
